/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconDownload,
  IconRefresh,
  IconRotateClockwise,
  IconSearch,
  IconChevronLeft,
  IconChevronRight,
  IconAdjustmentsHorizontal,
  IconPlus,
  IconUpload,
  IconArrowsLeftRight,
  IconX,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import AllOrderTable from "./AllOrderTable";
import formatDate from "../../../utilities/helper";
import * as XLSX from "xlsx";
import { useIntegrationChannelsAll } from "../../../apis/integrationApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useFetchDarkStoreAllData } from "../../../apis/darkStoreApi";
import NewSelectInput from "../../../components/NewSelectInput";
import {
  useFetchOrderOperations,
  useCreateOrderMutation,
} from "../../../apis/operationApis";
import AddOrdersModals from "./AddOrdersModals";
import OrderBulkUploadModals from "./OrderBulkUploadModals";
import ReverseOrderModal from "./ReverseOrderModal";
import FilterSidebar from "./FilterSidebar";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { useLanguage } from "../../../context/LanguageContext";

import { useSelector } from "react-redux";
import { usePermission } from "../../../hooks/usePermissionTabs";
import MessageModal from "../../../components/MessageModal";
import NewCommonModalCenter from "../../../components/common/NewCommonModalCenter";
import BulkStatusRevertModal from "./BulkStatusRevertModal";
import BulkForwardModal from "./BulkForwardModal";
import BulkPdfModal from "./LablePrintBulkpdf";
import OrderSplitModal from "../../OrderManagement/_components/OrderSplitModal";
import ActionSidebar from "./ActionSidebar";
import { OrderSummaryStats } from "../../OrderManagement/_components/OrderSummaryStats";

import {
  useOrderReverseMutation,
  useOrderStatusRevertMutation,
  useOrderRouteSyncMutation,
} from "../../../apis/orderApis";
import DropdownFilter from "../../../components/common/DropdownFilter";

type Props = {
  pending?: boolean;
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
  title?: string;
  onRefresh?: () => void;
};

export const OrderActionDrop = [
  { id: "1", value: "BULK", text: "Bulk Order" },
  { id: "2", value: "REVERSE", text: "Reverse Order" },
];

export default function AllOrders({
  pending,
  setHeaderActions,
  setHeaderButtons,
  title = "Order Create Management View",
  onRefresh,
}: Props) {
  const { allowed: canCreateOrder } = usePermission("orders", "create");
  const { allowed: canUpdateOrder } = usePermission("orders", "update");
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [selectedTag, setSelectedTag] = useState<string>("All");
  const [selectedMerchant, setSelectedMerchant] = useState<string>("All");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("All");
  const [selectedStatus, setSelectedStatus] = useState<string>("All");

  const [orderAction, setOrderAction] = useState("");

  // Modal states
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [orderBulkResponse, setOrderBulkResponse] = useState<any>(null);
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  /* Sidebar details & columns shrink hooks moved down */

  // Columns visibility
  const [columns, setColumns] = useState([
    { id: 1, text: "S/N", value: "sn", isShow: false },
    { id: 2, text: "TAG", value: "tag", isShow: true },
    { id: 3, text: "DATE", value: "createdDate", isShow: true },
    { id: 4, text: "PLATFORM", value: "platform", isShow: true },
    { id: 5, text: "AWB & REF", value: "awbRef", isShow: true },
    { id: 6, text: "WAREHOUSE", value: "warehouse", isShow: true },
    { id: 7, text: "ROUTE", value: "route", isShow: true },
    {
      id: 8,
      text: "3PL SHIPPING DETAILS",
      value: "shippingDetails",
      isShow: true,
    },
    { id: 9, text: "ORIGIN", value: "origin", isShow: true },
    { id: 10, text: "DESTINATION", value: "destination", isShow: true },
    { id: 11, text: "RECEIVER", value: "receiverDetails", isShow: true },
    { id: 12, text: "PAYMENT", value: "paymentInfo", isShow: true },
    { id: 13, text: "MERCHANT", value: "merchant", isShow: true },
    { id: 14, text: "STATUS", value: "status", isShow: true },
    { id: 15, text: "ACTIONS", value: "actions", isShow: true },
  ]);

  const handleSetHeadings = (value: string | undefined, checked: boolean) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const backendStatus =
    selectedStatus === "All"
      ? undefined
      : selectedStatus === "New Order"
        ? "NEW_ORDER"
        : selectedStatus === "Deleted"
          ? "DELETED"
          : selectedStatus === "Reverse"
            ? "REVERSE_NEW"
            : selectedStatus === "Cancelled"
              ? "CANCELLED"
              : undefined;

  const [filters, setFilters] = useState<Array<Record<string, string>>>([]);

  const activeFilters = useMemo(() => {
    const combined = [...filters];
    const hasFilterKey = (key: string) =>
      combined.some((f) => Object.keys(f)[0] === key);

    if (selectedTag !== "All" && !hasFilterKey("orderType")) {
      combined.push({ orderType: selectedTag });
    }
    if (selectedMerchant !== "All" && !hasFilterKey("seller")) {
      combined.push({ seller: selectedMerchant });
    }
    if (
      selectedPlatform !== "All" &&
      !hasFilterKey("platformOrShippingCompany")
    ) {
      combined.push({ platformOrShippingCompany: selectedPlatform });
    }
    return combined;
  }, [selectedTag, selectedMerchant, selectedPlatform, filters]);

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useFetchOrderOperations(
    backendStatus,
    currentPage,
    limit,
    searchFor,
    activeFilters,
  );

  const { mutate: createOrderMutate, isPending: isCreateOrderPending } =
    useCreateOrderMutation();
  const orders = response?.data ?? [];
  const pagination = response?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const totalOrders = pagination?.total ?? 0;

  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);

  const { data: shippingPlatforms } = useIntegrationChannelsAll("shipping");
  const { data: clients } = useMerchantAllListData();
  const { data: darkStores } = useFetchDarkStoreAllData();

  // Profile from redux
  const profile = useSelector((state: any) => state.auth.profile);

  // Modal / UI states for actions
  const [showActionSidebar, setShowActionSidebar] = useState(false);

  // Bulk PDF states
  const [showBulkPdfModal, setShowBulkPdfModal] = useState(false);
  const [bulkPdfOrders, setBulkPdfOrders] = useState<any[]>([]);
  const [bulkPdfCompany, setBulkPdfCompany] = useState("");

  // Other action-related modals
  const [openBsrModal, setOpenBsrModal] = useState(false);
  const [openForwardModal, setOpenForwardModal] = useState(false);
  const [isReverseOrderWiseOpen, setIsReverseOrderWiseOpen] = useState(false);
  const [isRevertOrder, setIsRevertOrder] = useState(false);
  const [orderItemId, setOrderItemId] = useState<any>(null);
  const [splitModalOrder, setSplitModalOrder] = useState<any>(null);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);

  const openBsrModalToggle = () => setOpenBsrModal(!openBsrModal);
  const openForwardModalToggle = () => setOpenForwardModal(!openForwardModal);
  const openReverseOrderWiseModal = () => setIsReverseOrderWiseOpen(true);
  const closeReverseOrderWiseModal = () => setIsReverseOrderWiseOpen(false);
  const closeRevertOrder = () => setIsRevertOrder(false);

  const [selectedViewOrderAwb, setSelectedViewOrderAwb] = useState<
    string | null
  >(null);

  // Track initial/default columns to restore them when sidebar is closed
  const [defaultVisibleColumns, setDefaultVisibleColumns] = useState<string[]>(
    [],
  );
  useEffect(() => {
    if (columns.length > 0 && defaultVisibleColumns.length === 0) {
      const visible = columns
        .filter((col) => col.isShow)
        .map((col) => col.value);
      setDefaultVisibleColumns(visible);
    }
  }, [columns]);

  // Handle auto-shrinking table columns when right sidebar is open
  useEffect(() => {
    const isAnyPanelOpen =
      showNewOrderModal ||
      showBulkModal ||
      showReverseModal ||
      isSplitModalOpen ||
      !!selectedViewOrderAwb ||
      showActionSidebar;
    if (isAnyPanelOpen) {
      const essentialColumns = [
        "tag",
        "awbRef",
        "orderType",
        "shipment",
        "route",
        "platform",
        "merchant",
        "orderName",
        "status",
        "actions",
      ];
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          isShow: essentialColumns.includes(col.value),
        })),
      );
    } else if (defaultVisibleColumns.length > 0) {
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          isShow: defaultVisibleColumns.includes(col.value),
        })),
      );
    }
  }, [
    showNewOrderModal,
    showBulkModal,
    showReverseModal,
    isSplitModalOpen,
    selectedViewOrderAwb,
    showActionSidebar,
    defaultVisibleColumns,
  ]);

  // Mutually exclusive sidebar panels: opening one closes the others
  useEffect(() => {
    if (showNewOrderModal) {
      setShowBulkModal(false);
      setShowReverseModal(false);
      setIsSplitModalOpen(false);
      setSelectedViewOrderAwb(null);
      setShowActionSidebar(false);
    }
  }, [showNewOrderModal]);

  useEffect(() => {
    if (showBulkModal) {
      setShowNewOrderModal(false);
      setShowReverseModal(false);
      setIsSplitModalOpen(false);
      setSelectedViewOrderAwb(null);
      setShowActionSidebar(false);
    }
  }, [showBulkModal]);

  useEffect(() => {
    if (showReverseModal) {
      setShowNewOrderModal(false);
      setShowBulkModal(false);
      setIsSplitModalOpen(false);
      setSelectedViewOrderAwb(null);
      setShowActionSidebar(false);
    }
  }, [showReverseModal]);

  useEffect(() => {
    if (isSplitModalOpen) {
      setShowNewOrderModal(false);
      setShowBulkModal(false);
      setShowReverseModal(false);
      setSelectedViewOrderAwb(null);
      setShowActionSidebar(false);
    }
  }, [isSplitModalOpen]);

  useEffect(() => {
    if (selectedViewOrderAwb) {
      setShowNewOrderModal(false);
      setShowBulkModal(false);
      setShowReverseModal(false);
      setIsSplitModalOpen(false);
      setShowActionSidebar(false);
    }
  }, [selectedViewOrderAwb]);

  useEffect(() => {
    if (showActionSidebar) {
      setShowNewOrderModal(false);
      setShowBulkModal(false);
      setShowReverseModal(false);
      setIsSplitModalOpen(false);
      setSelectedViewOrderAwb(null);
    }
  }, [showActionSidebar]);

  // Mutations

  const { mutate: syncRouteMutate } = useOrderRouteSyncMutation();
  const [isRouteSyncing, setIsRouteSyncing] = useState(false);

  const syncOrderRoutes = () => {
    setIsRouteSyncing(true);
    syncRouteMutate(undefined, {
      onSuccess: (data: any) => {
        refetch();
        toast.success(
          data?.message || "Order routes synchronized successfully",
        );
        setIsRouteSyncing(false);
      },
      onError: (error: any) => {
        toast.error(error?.message || "Failed to sync order routes");
        setIsRouteSyncing(false);
      },
    });
  };

  const { mutate: reverseOrderMutate } = useOrderReverseMutation();
  const { mutate: revertOrderMutate } = useOrderStatusRevertMutation(
    orderItemId || "",
  );

  const handleReverseOrder = () => {
    reverseOrderMutate(
      { orderId: [orderItemId] },
      {
        onSuccess: (data) => {
          console.log(data, "data");
          toast.success("Order reversed successfully");
          refetch();
        },
        onError: (error) => {
          console.log(error, "error");
          toast.error(`${error}`);
        },
      },
    );
    setIsReverseOrderWiseOpen(false);
  };

  const handleRevertOrder = () => {
    revertOrderMutate(undefined, {
      onSuccess: (data) => {
        refetch();
        setIsRevertOrder(false);
        toast.success("Order reverted successfully");
      },
      onError: (error) => {
        console.log(error, "error");
        toast.error(`${error}`);
      },
    });
  };

  // Reset to page 1 on filter change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedOrders([]);
  }, [
    searchFor,
    selectedTag,
    selectedMerchant,
    selectedPlatform,
    selectedStatus,
    filters,
  ]);

  const uniqueMerchants = useMemo(() => {
    const names = new Set<string>();
    if (clients && Array.isArray(clients)) {
      clients.forEach((c: any) => {
        if (c.name) names.add(c.name);
      });
    }
    if (darkStores && Array.isArray(darkStores)) {
      darkStores.forEach((ds: any) => {
        if (ds.name) names.add(ds.name);
      });
    }
    return Array.from(names);
  }, [clients, darkStores]);

  const uniquePlatforms = useMemo(() => {
    const set = new Set<string>();
    set.add("Salla");
    set.add("Zid");
    if (shippingPlatforms) {
      shippingPlatforms.forEach((sp: any) => {
        if (sp.companyName) set.add(sp.companyName);
      });
    }
    return Array.from(set);
  }, [shippingPlatforms]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order: any) => {
      const matchesSearch =
        searchFor === "" ||
        order.awbNumber?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.referenceNumber
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.receiverMobile?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.receiverEmail?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.receiverCityName
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverCountryName
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverDistrictName
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverAddress
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverNationalAddress
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.senderName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.senderCityName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.warehouseName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.clientName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.platform?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.shippingCompany
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.shippingAwbNumber
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.paymentType?.toLowerCase().includes(searchFor.toLowerCase()) ||
        String(order.codAmount)
          .toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.status?.toLowerCase().includes(searchFor.toLowerCase()) ||
        (order.createdAt &&
          formatDate(order.createdAt)
            .toLowerCase()
            .includes(searchFor.toLowerCase())) ||
        order.orderType?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.olineItems?.some(
          (item: any) =>
            item.sku?.toLowerCase().includes(searchFor.toLowerCase()) ||
            item.itemName?.toLowerCase().includes(searchFor.toLowerCase()),
        );
      const matchesTag =
        selectedTag === "All" || order.orderType === selectedTag;
      const matchesMerchant =
        selectedMerchant === "All" ||
        order.clientName?.toLowerCase() === selectedMerchant.toLowerCase();
      const matchesPlatform =
        selectedPlatform === "All" ||
        order.platform?.toLowerCase() === selectedPlatform.toLowerCase() ||
        order.shippingCompany?.toLowerCase() === selectedPlatform.toLowerCase();
      const matchesStatus =
        selectedStatus === "All" ||
        (selectedStatus === "New Order" && order.status === "NEW_ORDER") ||
        (selectedStatus === "Deleted" && order.status === "DELETED") ||
        (selectedStatus === "Reverse" && order.status === "REVERSE_NEW") ||
        (selectedStatus === "Cancelled" && order.status === "CANCELLED");
      return (
        matchesSearch &&
        matchesTag &&
        matchesMerchant &&
        matchesPlatform &&
        matchesStatus
      );
    });
  }, [
    searchFor,
    selectedTag,
    selectedMerchant,
    selectedPlatform,
    selectedStatus,
    orders,
  ]);

  // Select / Deselect order
  const handleSelectOrder = (order: any) => {
    setSelectedOrders((prev) => {
      if (prev.some((o) => o.id === order.id))
        return prev.filter((o) => o.id !== order.id);
      return [...prev, order];
    });
  };

  // Select all shown orders
  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelectedOrders(filteredOrders);
    else setSelectedOrders([]);
  };

  // Reset search and filters
  const handleReset = () => {
    setSearchFor("");
    setShowSearch(false);
    setSelectedTag("All");
    setSelectedMerchant("All");
    setSelectedPlatform("All");
    setSelectedStatus("All");
    setSelectedOrders([]);
    setFilters([]);
    setCurrentPage(1);
    toast.success("Filters reset successfully");
  };

  const hasActiveFilters =
    searchFor !== "" ||
    selectedTag !== "All" ||
    selectedMerchant !== "All" ||
    selectedPlatform !== "All" ||
    selectedStatus !== "All" ||
    filters.length > 0;

  // Create Order handler
  const handleCreateOrder = () => {
    if (selectedOrders.length === 0) {
      toast.error("Please select at least one order");
      return;
    }
    const hasNonFM = selectedOrders.some((order) => order.orderType !== "FM");
    if (hasNonFM) {
      toast.error("Selected orders must be of type FM");
      return;
    }
    const hasNonNewOrder = selectedOrders.some(
      (order) => order.status !== "NEW_ORDER",
    );
    if (hasNonNewOrder) {
      toast.error("Selected orders must be of status New Order");
      return;
    }
    const orderPayload = {
      orders: selectedOrders,
      totalOrders: selectedOrders.length,
    };
    createOrderMutate(orderPayload, {
      onSuccess: (res) => {
        toast.success(res?.message || "Order created successfully!");
        setSelectedOrders([]);
        refetch();
        if (onRefresh) onRefresh();
      },
      onError: (err: any) => {
        const errMsg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to create order";
        toast.error(errMsg);
        console.error("Create order error:", err);
      },
    });
  };

  // Export static orders to XLSX
  const handleExportXLSX = () => {
    const dataToExport =
      selectedOrders.length > 0 ? selectedOrders : filteredOrders;
    if (dataToExport.length === 0) {
      toast.error("No data available to export");
      return;
    }
    const data = dataToExport.map((order: any, idx: number) => ({
      "S/N": idx + 1,
      Tag: order.orderType,
      "Order Date": new Date(order.createdAt).toLocaleDateString(),
      Platform: order.platform,
      "AWB Number": order.awbNumber,
      Reference: order.referenceNumber,
      Warehouse: order.warehouseName,
      Route: order.routeCode,
      "Shipping Company": order.shippingCompany,
      "Shipping AWB": order.shippingAwbNumber,
      Origin: order.senderCityName,
      "Destination Country": order.receiverCountryName,
      "Destination City": order.receiverCityName,
      "Destination District": order.receiverDistrictName,
      "Receiver Name": order.receiverName,
      "Receiver Mobile": order.receiverMobile,
      "Payment Mode": order.paymentType,
      "Payment Amount": order.codAmount,
      Merchant: order.clientName,
      Status: order.status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, "order_allocations_export.xlsx");
    toast.success("Exported successfully!");
  };

  const handlePrintAll = () =>
    toast.success("Sending document(s) to printer...");

  // Pagination helpers
  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setSelectedOrders([]);
  };

  const renderPageNumbers = () => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  useEffect(() => {
    if (!setHeaderButtons) return;

    setHeaderButtons(
      <OrderSummaryStats
        selectedCount={selectedOrders.length}
        totalCount={totalOrders}
      />,
    );

    return () => setHeaderButtons(null);
  }, [setHeaderButtons, selectedOrders.length, totalOrders]);

  const isAddYellow = !showActionSidebar && !showBulkModal && !showReverseModal;
  const showRightPanel =
    showNewOrderModal ||
    showBulkModal ||
    showReverseModal ||
    isSplitModalOpen ||
    showActionSidebar;

  return (
    <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
      <div
        className={showRightPanel ? "col-span-12 xl:col-span-7" : "col-span-12"}
      >
        {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
        <div className="bg-white rounded-3xl   overflow-visible">
          <div className="px-6 py-1.5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {/* Left side: Badge + Filters Shifted */}
              <div className="flex flex-wrap items-center gap-3">

                <div
                  className={`flex items-center gap-3 transition-all duration-500 ease-in-out ${
                    selectedTag === "FM"
                      ? "max-w-[1000px] opacity-100 visible"
                      : "max-w-0 opacity-0 invisible pointer-events-none overflow-hidden"
                  }`}
                >
                  {/* Status filter */}
                  <NewSelectInput
                    name="status-filter"
                    placeholder={t("Status")}
                    value={selectedStatus === "All" ? "" : selectedStatus}
                    setValue={(val) => setSelectedStatus(val || "All")}
                    selectOptions={[
                      {
                        id: "New Order",
                        value: "New Order",
                        text: t("New Order"),
                      },
                      { id: "Deleted", value: "Deleted", text: t("Deleted") },
                      { id: "Reverse", value: "Reverse", text: t("Reverse") },
                      {
                        id: "Cancelled",
                        value: "Cancelled",
                        text: t("Cancelled"),
                      },
                    ]}
                    className="w-fit"
                    removeDiv
                    compact
                  />

                  {/* Merchant filter */}
                  <NewSelectInput
                    name="merchant-filter"
                    placeholder={t("Merchants")}
                    value={selectedMerchant === "All" ? "" : selectedMerchant}
                    setValue={(val) => setSelectedMerchant(val || "All")}
                    selectOptions={uniqueMerchants.map((m: any) => ({
                      id: m,
                      value: m,
                      text: m,
                    }))}
                    className="w-fit"
                    removeDiv
                    compact
                  />

                  {/* Platform filter */}
                  <NewSelectInput
                    name="platform-filter"
                    placeholder={t("Platforms")}
                    value={selectedPlatform === "All" ? "" : selectedPlatform}
                    setValue={(val) => setSelectedPlatform(val || "All")}
                    selectOptions={uniquePlatforms.map((p: any) => ({
                      id: p,
                      value: p,
                      text: p,
                    }))}
                    className="w-fit"
                    removeDiv
                    compact
                  />
                </div>

                {/* Reset Button — only shown when a filter is active */}
              </div>

              {/* Right side: Export + Search + Advanced Filters + Selected Badge + Primary Button */}
              <div className="flex items-center flex-wrap gap-2.5">
                {hasActiveFilters && (
                  <button
                    onClick={handleReset}
                   className="w-8 h-8 flex items-center justify-center  border  border-slate-200  text-[#212121] rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer disabled:opacity-50"
                    title={t("Reset Filters")}
                  >
                    <IconRotateClockwise size={14} stroke={2.5} />
                  </button>
                )}
                <DropdownFilter
                  setValue={(val: any) => {
                    const tag = val || "All";
                    setSelectedTag(tag);
                    if (tag !== "FM") {
                      setSelectedStatus("All");
                      setSelectedMerchant("All");
                      setSelectedPlatform("All");
                      setFilters([]);
                    } else {
                      setShowSearch(false);
                    }
                  }}
                  value={selectedTag === "All" ? "" : selectedTag}
                  filterheadings={[
                    { id: "FM", value: "FM", type: "FM" },
                    { id: "LM", value: "LM", type: "LM" },
                  ]}
                />
                {/* Sliding Search */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-64 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          value={searchFor}
                          onChange={(e) => setSearchFor(e.target.value)}
                          placeholder={t("Search...")}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchFor("");
                            setShowSearch(false);
                          }}
                          className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          title={t("Clear & Close")}
                        >
                          <IconX size={14} stroke={2.5} />
                        </button>
                      </>
                    )}
                  </div>
                  {!showSearch && (
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Search")}
                    </div>
                  )}
                </div>

                {/* Sync Routes */}
                {canUpdateOrder && (
                  <div className="relative group">
                    <button
                      onClick={syncOrderRoutes}
                      disabled={isRouteSyncing}
                      className="w-8 h-8 flex items-center justify-center  border  border-slate-200  text-[#212121] rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer disabled:opacity-50"
                    >
                      <IconRefresh
                        size={17}
                        stroke={2.5}
                        className={isRouteSyncing ? "animate-spin" : ""}
                      />
                    </button>
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Sync Routes")}
                    </div>
                  </div>
                )}

                {/* Export */}
                <div className="relative group">
                  <button
                    onClick={handleExportXLSX}
                    className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  >
                    <IconDownload size={17} stroke={2.5} />
                  </button>
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Export to Excel")}
                  </div>
                </div>

                {/* Filter Sidebar toggle */}
                <div className="relative group">
                  <button
                    onClick={() => setShowFilter(true)}
                    className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  >
                    <IconAdjustmentsHorizontal size={17} stroke={2} />
                  </button>
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Advanced Filters")}
                  </div>
                </div>

                {/* Entries Limit Selector */}
                {response?.pagination?.totalPages &&
                  response.pagination.totalPages * limit > 10 && (
                    <EntriesSelector
                      value={limit}
                      options={[
                        10,
                        ...(response.pagination.totalPages * limit > 10
                          ? [20]
                          : []),
                        ...(response.pagination.totalPages * limit > 20
                          ? [30]
                          : []),
                        ...(response.pagination.totalPages * limit > 50
                          ? [50]
                          : []),
                        ...(response.pagination.totalPages * limit > 50
                          ? [100]
                          : []),
                      ]}
                      onChange={(val) => {
                        setLimit(val);
                        setCurrentPage(1);
                      }}
                    />
                  )}

                {/* Columns Hide/Show Filter */}
                <div className="relative group">
                  <ColumnsFilter
                    columnsData={columns}
                    setHeadings={handleSetHeadings}
                  />
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Columns View")}
                  </div>
                </div>

                {/* Actions Menu */}
                {canUpdateOrder && (
                  <div className="relative group">
                    <button
                      onClick={() => {
                        setShowActionSidebar(true);
                      }}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                        showActionSidebar
                          ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                          : "bg-white hover:bg-slate-50 border-slate-200 text-slate-700"
                      }`}
                    >
                      <svg
                        clipRule="evenodd"
                        fillRule="evenodd"
                        strokeLinejoin="round"
                        strokeMiterlimit="1"
                        width={24}
                        height={24}
                        fill="currentColor"
                        viewBox="0 0 510 510"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="m242.825 100.614c-.812-2.164-.686-4.661.558-6.817 2.208-3.824 7.105-5.136 10.929-2.929l18.735 10.817c2.475 1.43 4 4.071 4 6.929s-1.525 5.499-4 6.928l-18.735 10.817c-3.824 2.208-8.721.895-10.929-2.928-1.244-2.156-1.37-4.653-.558-6.817h-33.366c-4.415 0-8-3.585-8-8 0-4.416 3.585-8 8-8zm6.46 306.748h33.366c4.415 0 8 3.584 8 8 0 4.415-3.585 8-8 8h-33.366c.813 2.164.687 4.661-.558 6.817-2.208 3.823-7.104 5.136-10.928 2.928l-18.736-10.817c-2.475-1.429-4-4.07-4-6.928s1.525-5.499 4-6.928l18.736-10.817c3.824-2.208 8.72-.896 10.928 2.928 1.245 2.156 1.371 4.653.558 6.817zm138.497-146.147v-29.57c0-4.416 3.585-8 8-8 4.416 0 8 3.584 8 8v29.57c1.929-.469 4.035-.225 5.888.845 3.824 2.207 5.136 7.104 2.928 10.928l-9.887 17.126c-1.43 2.475-4.071 4-6.929 4s-5.499-1.525-6.928-4l-9.888-17.126c-2.207-3.824-.895-8.721 2.929-10.928 1.853-1.07 3.958-1.314 5.887-.845zm-276.846.376v29.57c0 4.416-3.585 8-8 8s-8-3.584-8-8v-29.57c-1.929.469-4.035.226-5.888-.844-3.824-2.208-5.136-7.105-2.928-10.929l9.888-17.126c1.429-2.475 4.07-4 6.928-4s-5.499 1.525-6.928 4l9.888 17.126c2.207 3.824.895 8.721-2.928 10.929-1.854 1.07-3.959 1.313-5.888.844zm-41.143-233.776h21.669c1.766-7.349 8.387-12.815 16.281-12.815s14.515 5.466 16.294 12.815h21.656v25.629h-75.9zm91.9 15.279h9.141c4.511 0 8.191 3.68 8.191 8.191v150.755c0 4.522-3.68 8.202-8.191 8.202h-126.171c-4.522 0-8.202-3.68-8.202-8.202v-150.755c0-4.511 3.68-8.191 8.202-8.191h9.13v10.35c0 8.836 7.163 16 16 16h75.9c8.837 0 16-7.164 16-16zm-40.99 54.007h31.316c4.415 0 8-3.585 8-8s-3.585-8-8-8h-31.316c-4.415 0-8 3.585-8 8s3.585 8 8 8zm0 24.857h31.316c4.415 0 8-3.585 8-8s-3.585-8-8-8h-31.316c-4.415 0-8 3.585-8 8s3.585 8 8 8zm0 41.058h31.316c4.415 0 8-3.585 8-8 0-4.416-3.585-8-8-8h-31.316c-4.415 0-8 3.584-8 8 0 4.415 3.585 8 8 8zm-22.68-73.084c0-1.39-1.128-2.518-2.518-2.518h-23.194c-1.391 0-2.518 1.128-2.518 2.518v23.194c0 1.391 1.127 2.518 2.518 2.518h23.194c1.39 0 2.518-1.127 2.518-2.518zm0 65.867c0-.655-.261-1.283-.724-1.747-.463-.463-1.091-.723-1.746-.723h-23.291c-.655 0-1.283.26-1.746.723-.463.464-.723 1.092-.723 1.747v23.29c0 .655.26 1.283.723 1.747.463.463 1.091.723 1.746.723h23.291c.655 0 1.283-.26 1.746-.723.463-.464.724-1.092.724-1.747zm22.68 32.074h31.316c4.415 0 8-3.585 8-8 0-4.416-3.585-8-8-8h-31.316c-4.415 0-8 3.584-8 8 0 4.415 3.585 8 8 8zm292.989-150.916c7.938 1.886 15.508 5.022 22.466 9.31l14.602-6.493 17.199 17.199-6.468 14.602c4.287 6.958 7.423 14.528 9.31 22.491l14.896 5.733v24.328l-14.896 5.733c-1.887 7.938-5.047 15.508-9.31 22.466l6.468 14.602-17.199 17.199-14.602-6.468c-6.933 4.287-14.528 7.423-22.466 9.31l-5.733 14.896h-24.329l-5.733-14.896c-7.962-1.887-15.532-5.047-22.49-9.31l-14.602 6.468-17.199-17.199 6.492-14.602c-4.287-6.933-7.423-14.528-9.334-22.466l-14.896-5.733v-24.328l14.896-5.733c1.911-7.963 5.047-15.533 9.334-22.491l-6.492-14.602 17.199-17.199 14.602 6.493c6.958-4.288 14.528-7.424 22.466-9.31l5.757-14.921h24.329zm-17.91 32.56c-23.446 0-42.433 18.987-42.433 42.434 0 23.446 18.987 42.458 42.433 42.458 23.447 0 42.458-19.012 42.458-42.458 0-23.447-19.011-42.434-42.458-42.434zm.014 425.483c-49.525 0-89.928-40.376-89.928-89.901 0-49.524 40.403-89.927 89.928-89.927 49.524 0 89.901 40.403 89.901 89.927 0 49.525-40.377 89.901-89.901 89.901zm-11.805-81.986-20.094-20.095c-5.204-5.204-13.655-5.204-18.86 0-5.205 5.232-5.205 13.656 0 18.86l29.538 29.565c5.204 5.205 13.655 5.205 18.86 0 17.814-17.814 35.413-35.816 53.146-53.71 5.178-5.231 5.151-13.655-.053-18.833-5.205-5.178-13.656-5.151-18.834.054zm-248.893-18.932c1.643 3.343 2.565 7.105 2.565 11.082 0 13.895-11.264 25.14-25.16 25.14-13.895 0-25.14-11.245-25.14-25.14 0-13.896 11.245-25.161 25.14-25.161 4.083 0 7.939.973 11.348 2.699l34.034-34.035-3.279-15.205c-.358-1.66.151-3.388 1.351-4.589l26.653-26.668c1.294-1.295 3.193-1.779 4.949-1.262 1.757.517 3.09 1.953 3.475 3.743l3.248 15.064c.413 1.921 1.914 3.421 3.834 3.835l15.068 3.247c1.789.386 3.225 1.719 3.742 3.475.518 1.756.034 3.654-1.26 4.949l-26.653 26.653c-1.201 1.2-2.929 1.71-4.589 1.352l-15.224-3.281zm60.842-17.5c3.086 8.969 4.763 18.586 4.763 28.582 0 48.565-39.614 88.199-88.2 88.199-48.565 0-88.2-39.634-88.2-88.199 0-48.586 39.635-88.2 88.2-88.2 10.097 0 19.807 1.711 28.854 4.857-2.613 4.476-3.511 9.825-2.392 15.015l4.507 20.893c-8.911-5.843-19.555-9.245-30.969-9.245-31.201 0-56.68 25.459-56.68 56.68 0 31.2 25.479 56.68 56.68 56.68 31.221 0 56.68-25.48 56.68-56.68 0-11.337-3.356-21.914-9.127-30.788l20.996 4.526c5.141 1.108 10.438.236 14.888-2.32z" />
                      </svg>
                    </button>
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Actions")}
                    </div>
                  </div>
                )}

                {/* Add Order */}
                {canCreateOrder && (
                  <div className="relative group">
                    <button
                      onClick={() => setShowNewOrderModal(true)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                        isAddYellow
                          ? "bg-[#FBD037] border-[#FBD037] text-[#131327] hover:bg-[#e2bd30]"
                          : "bg-white hover:bg-slate-50 border-slate-200 text-slate-700"
                      }`}
                    >
                      <IconPlus size={17} stroke={2.5} />
                    </button>
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("New Order")}
                    </div>
                  </div>
                )}

                {selectedOrders.length > 0 &&
                  canCreateOrder &&
                  selectedOrders.every(
                    (order: any) =>
                      order.orderType === "FM" && order.status === "NEW_ORDER",
                  ) && (
                    <button
                      onClick={handleCreateOrder}
                      disabled={isCreateOrderPending}
                      className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wide transition-all border select-none bg-[#FBD037] border-[#FBD037] text-[#131327] hover:bg-[#e2bd30] shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150 ${isCreateOrderPending ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {isCreateOrderPending ? t("Creating...") : t("Create Order")}
                    </button>
                  )}
              </div>
            </div>
          </div>

          {/* ── Table ────────────────────────────────────────────────── */}
          <div className="border-t border-slate-100">
            {filteredOrders.length > 0 ? (
              <AllOrderTable
                data={{ orders: filteredOrders }}
                columnsData={columns}
                currentPage={currentPage}
                limitPage={limit}
                isCheckboxEnabled={true}
                selectedOrders={selectedOrders}
                onSelectOrder={handleSelectOrder}
                onViewDetails={(awb) => navigate(`/orders/${awb}`)}
                showRightPanel={showRightPanel}
              />
            ) : (
              <div className="py-16 text-center bg-white">
                <p className="text-slate-400 text-sm font-semibold">
                  {t("No orders found matching the filter")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Sidebar Right Panel ────────────────────────────────────────── */}
      {showRightPanel && (
        <>
          {/* Backdrop on mobile */}
          <div
            className="fixed inset-0 bg-black/40 z-30 xl:hidden"
            onClick={() => {
              setShowNewOrderModal(false);
              setShowBulkModal(false);
              setShowReverseModal(false);
              setIsSplitModalOpen(false);
              setSplitModalOrder(null);
              setSelectedViewOrderAwb(null);
              setShowActionSidebar(false);
            }}
          />
          <div className="col-span-12 xl:col-span-5 space-y-4  mt-2 xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
            {showActionSidebar ? (
              <ActionSidebar
                onClose={() => {
                  setShowActionSidebar(false);
                }}
                selectedOrders={selectedOrders}
                setSelectedOrders={setSelectedOrders}
                orders={orders}
                refetch={refetch}
                onShowBulkPdf={(pdfOrders, companyName) => {
                  setBulkPdfOrders(pdfOrders);
                  setBulkPdfCompany(companyName);
                  setShowBulkPdfModal(true);
                }}
                onShowSplitOrder={(order) => {
                  setSplitModalOrder(order);
                  setIsSplitModalOpen(true);
                }}
              />
            ) : (
              <div className="flex flex-col bg-white/20 rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3.5  shrink-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-[#212121]">
                      {t("Create Order")}
                    </h2>
                    <div className="flex items-center gap-2">
                      {/* Bulk Upload */}
                      {canCreateOrder && (
                        <button
                          onClick={() => setShowBulkModal(true)}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-150 border shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                            showBulkModal
                              ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                              : "bg-white hover:bg-slate-50 border-slate-200 text-slate-700"
                          }`}
                        >
                          <IconUpload size={14} stroke={2.5} />
                        </button>
                      )}

                      {/* Reverse Order */}
                      {(canCreateOrder || canUpdateOrder) && (
                        <button
                          onClick={() => setShowReverseModal(true)}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-150 border shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                            showReverseModal
                              ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                              : "bg-white hover:bg-slate-50 border-slate-200 text-slate-700"
                          }`}
                        >
                          <IconArrowsLeftRight size={14} stroke={2.5} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewOrderModal(false);
                        setShowBulkModal(false);
                        setShowReverseModal(false);
                      }}
                      className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
                      title="Close"
                    >
                      <IconX size={18} />
                    </button>
                  </div>
                </div>
                {showNewOrderModal && (
                  <AddOrdersModals
                    onClose={() => setShowNewOrderModal(false)}
                    fetchData={refetch}
                    isSidebar
                  />
                )}
                {showBulkModal && (
                  <OrderBulkUploadModals
                    onClose={() => {
                      setShowBulkModal(false);
                      refetch();
                    }}
                    setOrderBulkResponse={setOrderBulkResponse}
                    isSidebar
                  />
                )}
                {showReverseModal && (
                  <ReverseOrderModal
                    onClose={() => setShowReverseModal(false)}
                    fetchData={refetch}
                    isSidebar
                  />
                )}
              </div>
            )}

            {isSplitModalOpen && splitModalOrder && (
              <OrderSplitModal
                open={isSplitModalOpen}
                onClose={() => {
                  setIsSplitModalOpen(false);
                  setSplitModalOrder(null);
                }}
                orderId={splitModalOrder.id}
                awbNumber={splitModalOrder.awbNumber}
                onSuccess={refetch}
                isSidebar
              />
            )}
          </div>
        </>
      )}
      {/* ── Pagination ───────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end px-2 pb-4 col-span-12">
          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
            >
              <IconChevronLeft size={14} stroke={2.5} />
            </button>
            {renderPageNumbers().map((p, i) =>
              p === "..." ? (
                <span
                  key={`ellipsis-${i}`}
                  className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => goToPage(p as number)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold transition shadow-sm ${
                    currentPage === p
                      ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
            >
              <IconChevronRight size={14} stroke={2.5} />
            </button>
          </div>
        </div>
      )}

      {/* ── Filter Sidebar (slide-in) ─────────────────────────────── */}
      {showFilter && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowFilter(false)}
          />
          <div className="relative w-[400px] h-full bg-white rounded-l-3xl shadow-lg overflow-auto transition-transform duration-300 ease-in-out">
            <FilterSidebar
              setFilters={(newFilters) => setFilters(newFilters)}
              onClose={() => setShowFilter(false)}
              currentFilters={filters}
            />
          </div>
        </div>
      )}

      {/* ── Actions Modals & Popups ──────────────────────────────── */}
      <NewCommonModalCenter
        title={"Bulk Status Revert"}
        open={openBsrModal}
        closeButtonText={"Cancel"}
        onClose={openBsrModalToggle}
        actionButtonText="Submit"
        actionButtonOnClick={openBsrModalToggle}
      >
        <BulkStatusRevertModal />
      </NewCommonModalCenter>

      <NewCommonModalCenter
        title={"Forward"}
        open={openForwardModal}
        closeButtonText={"Cancel"}
        onClose={openForwardModalToggle}
        actionButtonText="Submit"
        actionButtonOnClick={openForwardModalToggle}
      >
        <BulkForwardModal />
      </NewCommonModalCenter>

      {/* Bulk PDF Modal for Multiple Orders */}
      {showBulkPdfModal && (
        <BulkPdfModal
          open={showBulkPdfModal}
          onClose={() => {
            setShowBulkPdfModal(false);
            setSelectedOrders([]);
          }}
          orders={bulkPdfOrders}
          companyName={bulkPdfCompany}
        />
      )}

      <MessageModal
        open={isReverseOrderWiseOpen}
        onClose={closeReverseOrderWiseModal}
        title="Confirmation Required"
        subtitle="Are you sure you want to reverse this order?"
        img={"/img/inactive.svg"}
        showFooter={true}
        closeButtonText="Cancel"
        actionButtonText="Confirm"
        actionButtonOnClick={handleReverseOrder}
        size="md"
      />

      <MessageModal
        open={isRevertOrder}
        onClose={closeRevertOrder}
        title="Confirmation Required"
        subtitle="Are you sure you want to revert this order?"
        img={"/img/active.svg"}
        showFooter={true}
        closeButtonText="Cancel"
        actionButtonText="Confirm"
        actionButtonOnClick={handleRevertOrder}
        size="md"
      />
    </div>
  );
}
